import {useState, useEffect, useRef} from 'react';
import {DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS} from '../Chat/Row/ChatBubbleDotCom';

const useInterval = (callback: () => unknown, delay?: number | null) => {
  const savedCallback = useRef<() => unknown>();

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (delay !== null) {
      const interval = setInterval(() => savedCallback.current?.(), delay || 0);
      return () => clearInterval(interval);
    }

    return undefined;
  }, [delay]);
};

export type DotComWordStreamerProps = {
  // The text we want to stream, which may be incomplete.
  text: string;
  // The delay between emitting each word in milliseconds.
  wordDelayMs?: number;
  // Disable typing animation and show the full text immediately.
  disabled: boolean;
  // Invoked once we have finished typing all the text.
  onFinished?: () => void;
};

// Finds the index position right after the first word
const getFirstWordEndIndex = (text: string): number => {
  const firstSpaceIndex = text.indexOf(' ');
  return firstSpaceIndex > 0 ? firstSpaceIndex + 1 : text.length;
};

/**
 * How the typing animation works:
 * First Word: We always render the first word so the ChatBubble can render, otherwise it won't render because text is empty.
 * Second Word: Is typed 240ms after the first word. 200ms are for the chat bubble scale animation and 40ms are for first word delay.
 * Third word...n: Is typed 40ms after the previous word.
 * New Line: If the word ends with a new line, we add 240ms to the delay so the chat bubble can scale before the next word is typed.
 */

export const useDotComWordStreamer = (
  props: DotComWordStreamerProps,
): {
  textChunk: string;
} => {
  const {text, wordDelayMs = 40, disabled, onFinished} = props;

  // When the component renders for the first time and is disabled we just emit it all at once rather than word-by-word.
  // So if someone reloads the page after streaming was complete we don't start over again.
  const [cursorIndex, setCursorIndex] = useState(
    disabled ? text.length : getFirstWordEndIndex(text),
  );

  const [currentDelay, setCurrentDelay] = useState<number | null>(
    // Delay the first word appeareance so the chat bubble animation has time to finish.
    // When disabled we don't want to trigger the useInterval, so we set it to null.
    disabled ? null : DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS + wordDelayMs,
  );

  useInterval(() => {
    let nextCursorIndex = cursorIndex;
    let endsWithNewLine = false;

    while (nextCursorIndex < text.length) {
      if (text[nextCursorIndex] === '\n') {
        endsWithNewLine = true;
      }

      if (text[nextCursorIndex] === ' ' && nextCursorIndex !== cursorIndex) {
        break;
      }

      nextCursorIndex += 1;
    }

    setCursorIndex(nextCursorIndex);
    setCurrentDelay(
      endsWithNewLine
        ? DOT_COM_CHAT_BUBBLE_SCALE_ANIMATION_MS + wordDelayMs
        : wordDelayMs,
    );
  }, currentDelay);

  // If the streamer is disabled and we have rendered it all, we are finished
  // if isDone is false and we completed the text, we are waiting for more streaming text
  const hasFinished = disabled && cursorIndex === text.length;

  // We have to do this because the onFinished callback is not stable
  const savedOnFinished = useRef<() => unknown>();

  useEffect(() => {
    savedOnFinished.current = onFinished;
  });

  useEffect(() => {
    if (hasFinished) {
      setCurrentDelay(null);
    }

    if (cursorIndex >= text.length) {
      savedOnFinished.current?.();
    }
  }, [cursorIndex, text.length, savedOnFinished, hasFinished]);

  return {
    textChunk: text.slice(0, cursorIndex),
  };
};
